import { shipmentReference } from "@/lib/shipment-reference";
import { useMemo, useState } from "react";
import { Link } from "react-router";
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  ChevronUp,
  Clock,
  Package,
  PauseCircle,
  Plus,
  Undo2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { type Task, TaskType, TripStatus } from "@/api.types.v2";
import { useTrips, useCreateTrip, useTripById } from "@/api/trips";
import { useAssignTasksToTrip } from "@/api/tasks";
import { useGetDispatchCenters } from "@/api/dispatch-centers";
import { filterAndSortCentersForCarriers } from "@/components/dispatch-center-filter";
import { useCarrierScope } from "@/contexts/carrier-scope-context";
import { getTaskTypeDisplay } from "@/features/tasks/tasks.helper";
import {
  taskInstructionLabel,
  taskInstructions,
} from "@/features/dispatch/selectors";
import { getOperatorDisplayName } from "@/lib/safe-access";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { taskLane, LANE_META } from "./task-type-meta";
import { taskRefs, taskAddressLine } from "./task-labels";
import { taskWindow } from "./trip-stops";
import { CareIcons, CareInline } from "./care";
import { CapacityBar } from "./atoms";
import { taskIsOnCancellationHold } from "./work-hold";
import {
  BuildingVisitPlanner,
  type BuildingVisitDraft,
  verifiedBuildingVisitsForAssignment,
} from "./building-visit-planner";

interface AssignPanelProps {
  tasks: Task[];
  onClose: () => void;
}

const DEFAULT_CAPACITY = 90;
const MIN_PER_STOP = 4.5; // ETA-impact heuristic (preview estimate)

function DirGlyph({ type }: { type: Task["type"] }) {
  const lane = taskLane(type);
  const cls = "h-[13px] w-[13px]";
  const icon =
    lane === "pickup" ? (
      <ArrowUp className={cls} strokeWidth={2.6} />
    ) : lane === "return" ? (
      <Undo2 className={cls} strokeWidth={2.6} />
    ) : (
      <ArrowDown className={cls} strokeWidth={2.6} />
    );
  return (
    <Badge
      className="size-[22px] shrink-0 border-0 p-0 text-white [&>svg]:size-[13px]"
      style={{ background: LANE_META[lane].color }}
      title={getTaskTypeDisplay(type)}
    >
      {icon}
    </Badge>
  );
}

const normalizeContact = (value?: string | null) =>
  value?.trim().replace(/\s+/g, " ").toLowerCase();

export function AssignPanel({ tasks, onClose }: AssignPanelProps) {
  const { carrierIds, dispatchCenterIds: scopedCenterIds } = useCarrierScope();
  const { data: tripsResponse } = useTrips({ status: "ACTIVE,PLANNED" });
  const { data: centers } = useGetDispatchCenters();
  // Centers the dispatcher can currently target.
  const scopedCenters = filterAndSortCentersForCarriers(
    centers ?? [],
    carrierIds
  ).filter(
    (c) => scopedCenterIds.length === 0 || scopedCenterIds.includes(c.id)
  );
  const targets = useMemo(
    () =>
      (tripsResponse?.data ?? []).filter(
        (t) => t.status === TripStatus.ACTIVE || t.status === TripStatus.PLANNED
      ),
    [tripsResponse]
  );

  // "new" = build a fresh trip from the selection; else an existing trip id.
  const [target, setTarget] = useState<string>("new");
  const [buildingVisits, setBuildingVisits] = useState<BuildingVisitDraft[]>(
    []
  );
  const { data: targetTrip } = useTripById(target === "new" ? "" : target);
  const assignableTasks = useMemo(
    () => tasks.filter((task) => !taskIsOnCancellationHold(task)),
    [tasks]
  );
  const [sel, setSel] = useState<string[]>(() =>
    assignableTasks.map((task) => task.id)
  );
  const [expanded, setExpanded] = useState<string | null>(null);

  const assign = useAssignTasksToTrip({
    onSuccess: () => {
      toast.success("Assigned", { description: "Tasks added to the trip." });
      onClose();
    },
    onError: () => toast.error("Failed to assign tasks."),
  });
  const createTrip = useCreateTrip({});

  const toggle = (task: Task) => {
    if (taskIsOnCancellationHold(task)) return;
    setBuildingVisits((current) =>
      current.map((visit) => ({
        ...visit,
        taskIds: visit.taskIds.filter((id) => id !== task.id),
        confirmed: false,
      }))
    );
    setSel((current) =>
      current.includes(task.id)
        ? current.filter((id) => id !== task.id)
        : [...current, task.id]
    );
  };

  const changeTarget = (value: string) => {
    setTarget(value);
    setBuildingVisits((current) =>
      current.map((visit) => ({ ...visit, waypointId: "", confirmed: false }))
    );
  };

  const tgt = targets.find((t) => t.id === target);
  const stopsCount =
    sel.length -
    buildingVisits.reduce(
      (count, visit) =>
        count +
        Math.max(0, visit.taskIds.filter((id) => sel.includes(id)).length - 1) +
        (visit.waypointId && visit.taskIds.some((id) => sel.includes(id))
          ? 1
          : 0),
      0
    );
  const n = sel.length;

  // impact preview
  const existingLoad = tgt?.totalTasks ?? 0;
  const tgtCapNum = tgt?.vehicle?.capacity
    ? Object.values(tgt.vehicle.capacity).find(
        (v): v is number => typeof v === "number" && v > 0
      )
    : undefined;
  const capacity = tgtCapNum ?? DEFAULT_CAPACITY;
  const loadAfter = existingLoad + n;
  const etaImpact = Math.round(n * MIN_PER_STOP);

  const [submitting, setSubmitting] = useState(false);
  const busy = submitting || assign.isPending || createTrip.isPending;

  const confirm = async () => {
    if (n === 0) return;
    const verifiedBuildingVisits = verifiedBuildingVisitsForAssignment(
      buildingVisits,
      sel,
      target,
      targetTrip,
      tasks
    );
    if (!verifiedBuildingVisits) {
      toast.error("Confirm each building visit and select its tasks.");
      return;
    }
    setSubmitting(true);
    try {
      let tripId = target;
      if (target === "new") {
        const scopedTaskIds = tasks
          .filter(
            (task) =>
              sel.includes(task.id) && task.type !== TaskType.CARRIER_DROPOFF
          )
          .map((task) => task.dispatchCenterId);
        const taskCenters = new Set(
          scopedTaskIds.filter((id): id is string => Boolean(id))
        );
        if (scopedTaskIds.some((id) => !id) || taskCenters.size > 1) {
          toast.error("Selected tasks must share one dispatch center.");
          return;
        }
        const dispatchCenterId =
          taskCenters.values().next().value ?? scopedCenters[0]?.id;
        if (!dispatchCenterId) {
          toast.error("No dispatch center", {
            description: "Can't create a trip — no dispatch center in scope.",
          });
          setSubmitting(false);
          return;
        }
        if (!scopedCenters.some((center) => center.id === dispatchCenterId)) {
          toast.error("Switch to the selected tasks' dispatch center.");
          return;
        }
        const trip = await createTrip.mutateAsync({ dispatchCenterId });
        tripId = trip.id;
      }
      assign.mutate({
        tripId,
        taskIds: sel,
        ...(verifiedBuildingVisits.length ? { verifiedBuildingVisits } : {}),
      });
    } catch {
      toast.error("Couldn't build the trip.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="flex items-center gap-2 border-b border-zinc-200 px-3.5 py-3">
        <Package className="h-4 w-4 text-indigo-600" />
        <span className="text-[14px] font-bold text-zinc-900">
          Selected tasks → trip
        </span>
        <span className="rounded-full bg-indigo-100 px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-indigo-700">
          {n}/{tasks.length}
        </span>
        <div className="flex-1" />
        <Button
          variant="ghost"
          size="icon"
          className="size-7 text-zinc-400 hover:bg-zinc-100"
          onClick={onClose}
        >
          <X className="size-[15px]" />
        </Button>
      </div>

      {/* target chooser */}
      <div className="flex flex-col gap-2.5 border-b border-zinc-100 px-3.5 py-3">
        <div className="text-[11px] font-semibold uppercase tracking-wider text-zinc-400">
          Add to trip
        </div>
        <button
          onClick={() => changeTarget("new")}
          className={
            "flex h-9 items-center gap-2 rounded-lg border px-2.5 text-[12.5px] font-semibold transition-colors " +
            (target === "new"
              ? "border-indigo-500 bg-indigo-50 text-indigo-700"
              : "border-zinc-200 text-zinc-700 hover:bg-zinc-50")
          }
        >
          <Plus className="h-3.5 w-3.5" /> New trip from selection
        </button>
        <Select
          value={target === "new" ? "" : target}
          onValueChange={changeTarget}
        >
          <SelectTrigger
            className={
              "h-9 w-full rounded-lg bg-white text-[12.5px] font-medium " +
              (target !== "new"
                ? "border-indigo-500 text-zinc-900"
                : "border-zinc-200 text-zinc-500")
            }
          >
            <SelectValue placeholder="Open trip…" />
          </SelectTrigger>
          <SelectContent>
            {targets.map((t) => (
              <SelectItem key={t.id} value={t.id} className="text-[12.5px]">
                #{t.tripNumber} ·{" "}
                {t.operator ? getOperatorDisplayName(t.operator) : "Unassigned"}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* impact preview */}
        <div className="mt-0.5 grid grid-cols-2 gap-2">
          <div className="rounded-lg border border-zinc-200 px-2.5 py-2">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
              Load after
            </div>
            <CapacityBar load={loadAfter} capacity={capacity} />
          </div>
          <div className="rounded-lg border border-zinc-200 px-2.5 py-2">
            <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
              ETA impact
            </div>
            <div className="flex items-center gap-1 text-[13px] font-bold tabular-nums text-zinc-800">
              <Clock className="h-3.5 w-3.5 text-zinc-400" />+{etaImpact} min
            </div>
          </div>
        </div>
      </div>

      {/* select all / clear */}
      <div className="flex items-center gap-2 border-b border-zinc-100 px-3.5 py-2">
        <button
          className="text-[11.5px] font-semibold text-indigo-600 hover:underline"
          onClick={() => setSel(assignableTasks.map((task) => task.id))}
        >
          All
        </button>
        <span className="text-zinc-300">·</span>
        <button
          className="text-[11.5px] font-semibold text-zinc-500 hover:underline"
          onClick={() => {
            setSel([]);
            setBuildingVisits([]);
          }}
        >
          Clear
        </button>
        <div className="flex-1" />
        <span className="text-[11px] text-zinc-400">
          Up to {stopsCount} new stops
        </span>
      </div>

      {/* decision-first rows */}
      <div className="flex-1 overflow-y-auto px-2 py-1.5">
        {tasks.map((tk) => {
          const held = taskIsOnCancellationHold(tk);
          const checked = sel.includes(tk.id);
          const isOpen = expanded === tk.id;
          const refs = taskRefs(tk);
          const win = taskWindow(tk);
          const contact = tk.contactName?.trim();
          const contactAlreadyShown = tk.shipment?.addresses?.some(
            (address) =>
              (address.type === "FROM" || address.type === "TO") &&
              [address.contactName, address.contactCompany].some(
                (value) => normalizeContact(value) === normalizeContact(contact)
              )
          );
          return (
            <div
              key={tk.id}
              className={
                "mb-1.5 rounded-lg border transition-colors " +
                (held
                  ? "border-zinc-200 bg-zinc-100 opacity-70"
                  : checked
                    ? "border-zinc-200 bg-white"
                    : "border-zinc-100 bg-zinc-50/50 opacity-60")
              }
            >
              <div className="flex items-center gap-2 px-2 py-2">
                <button
                  type="button"
                  aria-label={`Select TSK-${tk.taskNumber}`}
                  aria-pressed={checked}
                  onClick={() => toggle(tk)}
                  disabled={held}
                  className="grid h-11 w-11 shrink-0 place-items-center rounded lg:h-[17px] lg:w-[17px]"
                >
                  <span
                    className={
                      "grid h-[17px] w-[17px] place-items-center rounded " +
                      (held
                        ? "border border-zinc-300 bg-zinc-200 text-zinc-500"
                        : checked
                          ? "bg-indigo-600 text-white"
                          : "border border-zinc-300 bg-white text-transparent")
                    }
                  >
                    {held ? (
                      <PauseCircle className="h-[12px] w-[12px]" />
                    ) : (
                      <Check className="h-[11px] w-[11px]" strokeWidth={3} />
                    )}
                  </span>
                </button>
                <DirGlyph type={tk.type} />
                <button
                  aria-expanded={isOpen}
                  className="min-w-0 flex-1 text-left"
                  onClick={() => setExpanded(isOpen ? null : tk.id)}
                >
                  <span className="block break-words font-mono text-[13px] font-semibold text-zinc-900">
                    {shipmentReference(refs.shipmentNumber)}
                  </span>
                  {refs.orderNumber && (
                    <span className="block font-mono text-[11px] text-zinc-600">
                      ORD-{refs.orderNumber}
                    </span>
                  )}
                  <span className="block text-[11px] text-zinc-600">
                    {getTaskTypeDisplay(tk.type)}
                  </span>
                </button>
                <CareIcons task={tk} size={18} />
                {held && (
                  <Badge variant="secondary" title="Resolve in Cancellations">
                    On hold
                  </Badge>
                )}
                <button
                  type="button"
                  aria-label={`Details for TSK-${tk.taskNumber}`}
                  aria-expanded={isOpen}
                  className="grid h-11 w-11 shrink-0 place-items-center rounded text-zinc-400 hover:bg-zinc-100 lg:h-6 lg:w-6"
                  onClick={() => setExpanded(isOpen ? null : tk.id)}
                >
                  {isOpen ? (
                    <ChevronUp className="h-3.5 w-3.5" />
                  ) : (
                    <ChevronDown className="h-3.5 w-3.5" />
                  )}
                </button>
              </div>
              <div className="space-y-1 px-3 pb-2 lg:pl-9 lg:pr-2.5">
                <div className="break-words text-[13px] font-medium leading-snug text-zinc-900">
                  {taskAddressLine(tk)}
                  {(tk.addressLine2 ?? tk.address?.line2) &&
                    ` · Unit ${tk.addressLine2 ?? tk.address?.line2}`}
                </div>
                {taskInstructions(tk) && (
                  <div className="break-words text-[12px] text-amber-700">
                    {taskInstructionLabel(tk)}: {taskInstructions(tk)}
                  </div>
                )}
                {win && (
                  <div className="break-words text-[12px] font-medium text-amber-700">
                    {win}
                  </div>
                )}
              </div>
              <dl className="space-y-1 px-3 pb-2.5 text-xs lg:pl-9 lg:pr-2.5">
                {["FROM", "TO"].map((type) => {
                  const contact = tk.shipment?.addresses?.find(
                    (address) => address.type === type
                  );
                  const name = contact?.contactName?.trim();
                  const company = contact?.contactCompany?.trim();
                  return (
                    <div key={type} className="flex gap-2">
                      <dt className="w-8 shrink-0 text-zinc-500">
                        {type === "FROM" ? "From" : "To"}
                      </dt>
                      <dd className="min-w-0 break-words text-zinc-700">
                        {name || "Not provided"}
                        {company && company !== name && (
                          <span className="block text-zinc-500">{company}</span>
                        )}
                      </dd>
                    </div>
                  );
                })}
              </dl>
              {isOpen && (
                <div className="fadeup min-w-0 break-words px-3 pb-2.5 lg:pl-9 lg:pr-2.5 [&_span]:min-w-0">
                  <div className="font-mono text-[11px] text-zinc-500">
                    TSK-{tk.taskNumber}
                  </div>
                  {contact && !contactAlreadyShown && (
                    <div className="mt-1 text-[12px] text-zinc-700">
                      <span className="mr-2 text-zinc-500">Stop contact</span>
                      {contact}
                    </div>
                  )}
                  {held && (
                    <Link
                      to="/fulfillment/cancellations"
                      className="mt-1 block text-[11px] font-semibold text-zinc-600 underline"
                    >
                      Cancellation review — assignment disabled
                    </Link>
                  )}
                  <CareInline task={tk} />
                </div>
              )}
            </div>
          );
        })}
        <BuildingVisitPlanner
          tasks={tasks}
          selectedTaskIds={sel}
          target={target}
          targetTrip={targetTrip}
          visits={buildingVisits}
          onChange={setBuildingVisits}
        />
      </div>

      {/* footer */}
      <div className="border-t border-zinc-200 px-3.5 py-3">
        <button
          onClick={confirm}
          disabled={n === 0 || busy || (target !== "new" && !tgt)}
          className="flex h-10 w-full items-center justify-center gap-2 rounded-lg bg-indigo-600 text-[13px] font-semibold text-white transition-colors hover:bg-indigo-700 disabled:opacity-40"
        >
          <Check className="h-[15px] w-[15px]" strokeWidth={2.6} />
          {busy
            ? "Working…"
            : target === "new"
              ? "Build into trip"
              : `Confirm → #${tgt?.tripNumber ?? ""}`}
          <span className="tabular-nums">({n})</span>
        </button>
      </div>
    </div>
  );
}
